import { forwardRef } from "react";
import { motion, usePresenceData } from "motion/react";
import PropTypes from "prop-types";
import { Button } from "react-aria-components";
import { useSelector } from "react-redux";

import { useMobileFilterSelect } from "../../../../../components/Filtering/FilteringProvider";
import { twMerge } from "../../../../../helpers/twMerge";
import { getModalFilteringControls } from "../../../../../selectors";

const ENTRY_ANIMATION_DURATION = 0.2;
const EXIT_ANIMATION_DURATION = 0.2;
const ENTRY_ANIMATION_DISTANCE = 500;
const EXIT_ANIMATION_DISTANCE = 1000;

export const StepContent = forwardRef(
	({ currentStep, onSelect, ...props }, ref) => {
		const modalFilteringControls = useSelector(getModalFilteringControls);
		const { icon, title, options } = modalFilteringControls[currentStep];
		const { selectedOption, handleChange } = useMobileFilterSelect(
			modalFilteringControls[currentStep],
		);

		const handleOptionSelect = (value) => {
			handleChange({ target: { value } });
			onSelect(value);
		};

		return (
			<StepAnimation ref={ref} {...props}>
				<div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:md:max-w-[330px] tw:md:shrink-0 tw:md:items-start">
					<img
						src={icon}
						alt={title}
						className="tw:h-[56px] tw:w-[56px] tw:rounded-full tw:shadow-md tw:md:h-[64px] tw:md:w-[64px]"
					/>
					<h2 className="tw:text-center tw:text-2xl tw:leading-8 tw:font-bold tw:tracking-[-0.24px] tw:text-[#212121] tw:md:text-left tw:md:text-[28px] tw:md:leading-9 tw:md:tracking-[-0.28px]">
						{title}
					</h2>
				</div>
				<div className="tw:mt-12 tw:flex tw:flex-auto tw:flex-col tw:items-center tw:gap-4 tw:md:m-0 tw:md:w-full tw:md:flex-row tw:md:flex-wrap tw:md:justify-center tw:md:gap-5 tw:md:pl-[42px]">
					{options.map((option) => {
						if (option.value === "default") return null;

						return (
							<StepButton
								key={option.value}
								onClick={() => handleOptionSelect(option.value)}
								isSelected={selectedOption === option.value}
							>
								{option.label}
							</StepButton>
						);
					})}
				</div>
			</StepAnimation>
		);
	},
);

StepContent.displayName = "StepContent";

StepContent.propTypes = {
	currentStep: PropTypes.number.isRequired,
	onSelect: PropTypes.func.isRequired,
};

export const StepAnimation = forwardRef(({ className, ...props }, ref) => {
	const presenceDirection = usePresenceData();

	return (
		<motion.div
			ref={ref}
			initial={{
				opacity: 0,
				x: presenceDirection * ENTRY_ANIMATION_DISTANCE,
			}}
			animate={{
				opacity: 1,
				x: 0,
				transition: {
					ease: "easeIn",
					duration: ENTRY_ANIMATION_DURATION,
				},
			}}
			exit={{
				opacity: 0,
				x: presenceDirection * -EXIT_ANIMATION_DISTANCE,
				transition: {
					ease: "easeOut",
					duration: EXIT_ANIMATION_DURATION,
				},
			}}
			className={twMerge(
				"tw:absolute tw:flex tw:size-full tw:flex-col tw:px-4 tw:pt-6 tw:md:w-full tw:md:flex-row tw:md:items-center tw:md:p-0",
				className,
			)}
			{...props}
		/>
	);
});

StepAnimation.displayName = "StepAnimation";

StepAnimation.propTypes = {
	className: PropTypes.string,
};

const StepButton = ({
	children,
	onClick,
	isSelected = false,
	className,
	...props
}) => (
	<Button
		onPress={onClick}
		className={twMerge(
			"tw:inline-flex tw:w-full tw:cursor-pointer tw:appearance-none tw:items-center tw:justify-center tw:rounded-[12px] tw:border-[1.5px] tw:border-transparent tw:bg-white tw:p-4 tw:text-center tw:align-middle tw:whitespace-nowrap tw:text-[#212121] tw:shadow-[0px_0px_2px_0px_rgba(24,39,75,0.18),0px_6px_12px_-6px_rgba(24,39,75,0.12),0px_8px_24px_-4px_rgba(24,39,75,0.08)] tw:outline-hidden tw:transition-all tw:ease-in-out tw:select-none tw:focus-visible:ring-3 tw:focus-visible:ring-blue-500/50 tw:data-[hovered=true]:shadow-[0px_4px_24px_6px_rgba(23,23,23,0.12),0px_0px_1px_0px_rgba(23,23,23,0.16)] tw:data-[pressed=true]:border-[#0756C0] tw:data-[pressed=true]:shadow-none tw:data-[hovered=true]:data-[pressed=true]:shadow-none tw:md:w-[calc(50%-10px)]",
			isSelected ? "tw:border-[#0756C0] tw:shadow-none" : "",
			className,
		)}
		{...props}
	>
		<span className="tw:pt-1 tw:text-base tw:leading-5 tw:font-medium tw:md:text-sm">
			{children}
		</span>
	</Button>
);

StepButton.propTypes = {
	children: PropTypes.node.isRequired,
	onClick: PropTypes.func.isRequired,
	isSelected: PropTypes.bool,
	className: PropTypes.string,
};
